import * as RadixDialog from '@radix-ui/react-dialog';
import {
  ElementType,
  forwardRef,
  HTMLAttributes,
  ReactElement,
  ReactNode,
  Ref,
} from 'react';

import { cn } from '@/lib/utils/tailwind';

import type { AsElement, PolymorphicProps } from '@/types';

type DialogTriggerProps<T extends ElementType = 'button'> = PolymorphicProps<
  T,
  {
    children?: ReactNode;
    className?: string;
  }
>;

/*
required to create an inner & outer component due to radix needing forwardRef
and the fact we use generics
 */
type DialogTriggerBaseProps = {
  as?: ElementType;
  children?: ReactNode;
  className?: string;
  ref?: Ref<HTMLElement>;
} & HTMLAttributes<HTMLElement>;

const DialogTriggerBase = forwardRef<HTMLElement, DialogTriggerBaseProps>(
  ({ as, className, children, ...props }, ref) => {
    const Component: ElementType = as || 'button';
    return (
      <RadixDialog.Trigger asChild>
        <Component ref={ref} className={cn([], className)} {...props}>
          {children}
        </Component>
      </RadixDialog.Trigger>
    );
  },
);
DialogTriggerBase.displayName = 'DialogTriggerBase';

const DialogTrigger = (<T extends ElementType = 'button'>(
  props: DialogTriggerProps<T> & {
    ref?: Ref<AsElement<T>>;
  },
): ReactElement => {
  return <DialogTriggerBase {...props} />;
}) as <T extends ElementType = 'button'>(
  props: DialogTriggerProps<T> & {
    ref?: Ref<AsElement<T>>;
  },
) => ReactElement;

Object.defineProperty(DialogTrigger, 'displayName', {
  value: 'DialogTrigger',
  configurable: true,
});

export { DialogTrigger, type DialogTriggerProps };
